import { useState } from 'react'
import type { Dispatch, SetStateAction } from 'react'
import { useNavigate } from 'react-router-dom'
import { MessageCircle } from 'lucide-react'
import { useHotelProfile, useAvailability } from '../api/hooks'
import { getPrimaryImage } from '../api/client'

// Editorial fallback when the hotel hasn't supplied its own hero image.
// Wikimedia Commons — freely licensed, hotlink-friendly, and on-brand:
// Comayagua Cathedral viewed from the central park.
const HERO_FALLBACK_IMAGE =
  'https://upload.wikimedia.org/wikipedia/commons/thumb/a/a5/Comayagua_Cathedral_View_from_the_park.jpg/1920px-Comayagua_Cathedral_View_from_the_park.jpg'

// Curated marquee fallback — real Hotel Palmerola photography, one shot
// per room configuration so the strip visually represents the inventory.
const FALLBACK_MARQUEE_IMAGES = [
  { id: -1, image: '/rooms/sencilla.jpg', caption: 'Habitación Sencilla' },
  { id: -2, image: '/rooms/doble.jpg', caption: 'Habitación Doble' },
  { id: -3, image: '/rooms/superior.jpg', caption: 'Habitación Superior' },
  { id: -4, image: '/rooms/suite.jpg', caption: 'Suite' },
  { id: -5, image: '/rooms/presidencial.jpg', caption: 'Suite Presidencial' },
  { id: -6, image: '/rooms/triple.jpg', caption: 'Habitación Triple' },
  { id: -7, image: '/rooms/familiar.jpg', caption: 'Habitación Familiar' },
  { id: -8, image: HERO_FALLBACK_IMAGE, caption: 'Catedral de Comayagua' },
]
import type { AvailabilityResult, SearchState } from '../api/types'
import { searchForRoom } from '../components/roomOccupancy'
import { SearchBar } from '../components/SearchBar'
import { RoomDetailModal } from '../components/RoomDetailModal'
import { ImageSlider } from '../components/ImageSlider'
import { EssenceSection } from '../components/EssenceSection'
import { GalleryMarquee } from '../components/GalleryMarquee'
import { RoomsShowcase } from '../components/RoomsShowcase'
import { VisitComayaguaSection } from '../components/VisitComayaguaSection'

export function HomePage() {
  const navigate = useNavigate()
  const [selectedRoom, setSelectedRoom] = useState<AvailabilityResult | null>(null)
  const [search, setSearch] = useState<SearchState>(() => {
    const today = new Date()
    const nextDay = new Date(today)
    nextDay.setDate(today.getDate() + 1)
    const fmt = (d: Date) =>
      new Date(d.getTime() - d.getTimezoneOffset() * 60000).toISOString().slice(0, 10)
    return { check_in: fmt(today), check_out: fmt(nextDay), adults: 1, children: 0, adultsManuallySet: false }
  })

  const { data: hotel } = useHotelProfile()
  const availability = useAvailability()
  const quotedSearch = availability.variables
  const quoteIsCurrent = availability.isSuccess && quotedSearch &&
    quotedSearch.check_in === search.check_in && quotedSearch.check_out === search.check_out &&
    quotedSearch.adults === search.adults && quotedSearch.children === search.children &&
    quotedSearch.adultsManuallySet === search.adultsManuallySet
  const roomResults = quoteIsCurrent ? availability.data || [] : []
  const currentSelectedRoom = selectedRoom && roomResults.includes(selectedRoom) ? selectedRoom : null
  const handleSearchChange: Dispatch<SetStateAction<SearchState>> = (nextSearch) => {
    setSelectedRoom(null)
    setSearch(nextSearch)
  }
  const handleBook = (room: AvailabilityResult) => {
    if (!quoteIsCurrent || !quotedSearch || !roomResults.includes(room)) {
      setSelectedRoom(null)
      return
    }
    navigate('/booking', { state: { room, search: searchForRoom(quotedSearch, room) } })
  }
  // An uploaded hero image wins; otherwise the gallery rotates; otherwise
  // the primary gallery photo or the editorial fallback.
  const heroImage =
    hotel?.hero_image || getPrimaryImage(hotel?.gallery || []) || HERO_FALLBACK_IMAGE
  const showSlider = !hotel?.hero_image && (hotel?.gallery?.length ?? 0) > 1
  const content = hotel?.page_content

  const handleSearch = () => {
    setSelectedRoom(null)
    availability.mutate(search, {
      onSuccess: () => {
        setTimeout(() => {
          document.getElementById('rooms')?.scrollIntoView({ behavior: 'smooth', block: 'start' })
        }, 100)
      },
    })
  }

  return (
    <main>
      {/* Hero */}
      <section className="hero-section">
        {showSlider && hotel ? (
          <ImageSlider images={hotel.gallery} />
        ) : (
          <div
            className="hero-bg"
            style={heroImage ? { backgroundImage: `url(${heroImage})` } : undefined}
          />
        )}
        <div className="hero-overlay" />
        <div className="hero-vignette" />
        <div className="hero-content">
          {content?.hero.kicker && (
            <span className="hero-kicker">
              <span className="hero-kicker-line" aria-hidden />
              {content.hero.kicker}
              <span className="hero-kicker-line" aria-hidden />
            </span>
          )}
          <h1>{hotel?.hero_title || hotel?.hotel_name || ''}</h1>
          {hotel?.hero_subtitle && <p>{hotel.hero_subtitle}</p>}
          <SearchBar
            search={search}
            setSearch={handleSearchChange}
            onSearch={handleSearch}
            loading={availability.isPending}
            ctaLabel={hotel?.booking_cta_label}
          />
        </div>
        <a href="#about" className="hero-scroll-hint" aria-label="Bajar al contenido">
          <span />
        </a>
      </section>

      {/* Essence (About + service pillars merged) */}
      {hotel && <EssenceSection hotel={hotel} />}

      {/* Gallery marquee — uses hotel gallery if present, otherwise a small
          curated set of on-brand photos so the strip never disappears. */}
      <GalleryMarquee
        images={
          hotel?.gallery && hotel.gallery.length > 0
            ? hotel.gallery
            : FALLBACK_MARQUEE_IMAGES
        }
      />

      {/* Rooms — cinematic alternating layout */}
      <RoomsShowcase
        rooms={roomResults}
        search={search}
        onDetail={setSelectedRoom}
        onBook={handleBook}
        eyebrow={hotel?.rooms_section_title || 'Habitaciones'}
        heading={content?.rooms.heading}
        lede={hotel?.rooms_section_subtitle}
        errorState={
          availability.isError ? (
            <div className="notice error">No se pudo consultar la disponibilidad.</div>
          ) : null
        }
        neutralState={
          !availability.data || !quoteIsCurrent ? (
            <div className="notice">Usa el buscador de arriba para ver habitaciones disponibles.</div>
          ) : null
        }
        emptyState={
          availability.data && quoteIsCurrent && roomResults.length === 0 ? (
            <div className="notice">
              No hay inventario disponible para esas fechas. Prueba otra combinación.
            </div>
          ) : null
        }
      />

      {/* Vivir Comayagua */}
      {content && <VisitComayaguaSection content={content.visit} />}

      {/* Closing CTA — replaces the standalone contact section */}
      <section className="closing-cta" id="contact">
        <div className="closing-cta-inner">
          {content?.closing_cta.eyebrow && <span className="eyebrow">{content.closing_cta.eyebrow}</span>}
          {content?.closing_cta.title && <h2>{content.closing_cta.title}</h2>}
          <div className="closing-cta-actions">
            <a href="#rooms" className="primary-button">
              {content?.closing_cta.button_label || 'Ver habitaciones'}
            </a>
            {hotel?.whatsapp && (
              <a
                href={`https://wa.me/${hotel.whatsapp.replace(/[^0-9]/g, '')}`}
                target="_blank"
                rel="noopener noreferrer"
                className="secondary-button"
              >
                <MessageCircle size={16} /> WhatsApp
              </a>
            )}
          </div>
        </div>
      </section>

      {currentSelectedRoom && (
        <RoomDetailModal
          room={currentSelectedRoom}
          onClose={() => setSelectedRoom(null)}
          onBook={() => handleBook(currentSelectedRoom)}
        />
      )}
    </main>
  )
}
